/**
 * RSS Feed Page
 * 
 * This page demonstrates how to replace the RSSApp widget with a custom
 * RSS feed display component that keeps users on your website.
 * 
 * Original RSSApp widget code:
 * <rssapp-wall id="_pR8Eo8G0EoH0dxAJ"></rssapp-wall>
 * <script src="https://widget.rss.app/v1/wall.js" type="text/javascript" async></script>
 * 
 * This custom component:
 * - Fetches RSS feed data programmatically
 * - Displays articles in a custom UI (grid/list views)
 * - Opens article content in a modal (no redirects)
 * - Supports both RSSApp feeds and standard RSS/Atom feeds
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, ExternalLink, Settings } from 'lucide-react';
import RSSFeedDisplay from '../components/RSSFeedDisplay';
import SEO from '../components/SEO';

// Shared PageStyles component for consistent styling
const PageStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Azonix&display=swap');
    
    :root {
      --az: #a78bfa;
      --bg: #08080c;
      --glass: rgba(255, 255, 255, 0.03);
      --gb: rgba(167, 139, 250, 0.15);
    }
    
    .font-azonix {
      font-family: 'Azonix', sans-serif;
    }
    
    .glass-card {
      background: var(--glass);
      backdrop-filter: blur(20px);
      border: 1px solid var(--gb);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05);
    }
    
    .glow-border {
      position: relative;
    }
    
    .glow-border::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      padding: 1px;
      background: linear-gradient(135deg, rgba(167, 139, 250, 0.5), rgba(99, 102, 241, 0.3), rgba(139, 92, 246, 0.5));
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      pointer-events: none;
    }
    
    .gradient-text {
      background: linear-gradient(135deg, #a78bfa 0%, #6366f1 50%, #8b5cf6 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    
    .gradient-glow {
      background: linear-gradient(135deg, rgba(167, 139, 250, 0.1) 0%, rgba(99, 102, 241, 0.05) 100%);
    }
  `}</style>
);

// RSSApp Widget ID
const RSSAPP_WIDGET_ID = '_pR8Eo8G0EoH0dxAJ';

export default function RSSFeedPage() {
  const [showConfig, setShowConfig] = useState(false);
  const [config, setConfig] = useState({
    widgetId: RSSAPP_WIDGET_ID,
    title: 'Latest Articles',
    maxArticles: 9,
    showImages: true,
    compact: false
  });

  const handleConfigChange = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  return (
    <>
      <PageStyles />
      <SEO
        title="Latest Articles - ANIMAC"
        description="Read the latest articles from our RSS feed without leaving our website."
        url="/rss-feed"
        type="website"
      />
      
      <div className="min-h-screen relative overflow-hidden" style={{ background: '#08080c' }}>
        {/* Background effects */}
        <div className="absolute inset-0 gradient-glow"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: `
            radial-gradient(circle at 20% 80%, rgba(167, 139, 250, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(99, 102, 241, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.05) 0%, transparent 70%)
          `
        }}></div>

        {/* Header */}
        <header className="sticky top-0 z-40 glass-card backdrop-blur-md border-b border-[rgba(167,139,250,0.15)]">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{
                background: 'linear-gradient(135deg, #a78bfa 0%, #6366f1 50%, #8b5cf6 100%)'
              }}>
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-azonix font-bold text-white">
                  Latest Articles
                </h1>
                <p className="text-sm text-gray-400">Curated content from our RSS feed</p>
              </div>
            </div>
            
            <button
              onClick={() => setShowConfig(!showConfig)}
              className="p-2 rounded-lg glass-card hover:bg-[rgba(167,139,250,0.2)] transition-colors"
              title="Configuration"
            >
              <Settings className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </header>

        {/* Configuration Panel */}
        {showConfig && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="glass-card border-b border-[rgba(167,139,250,0.15)]"
          >
            <div className="max-w-7xl mx-auto px-4 py-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <ConfigInput
                  label="Widget ID"
                  value={config.widgetId}
                  onChange={(v) => handleConfigChange('widgetId', v)}
                  placeholder="Enter RSSApp widget ID"
                />
                <ConfigInput
                  label="Title"
                  value={config.title}
                  onChange={(v) => handleConfigChange('title', v)}
                  placeholder="Section title"
                />
                <ConfigInput
                  label="Max Articles"
                  type="number"
                  value={config.maxArticles}
                  onChange={(v) => handleConfigChange('maxArticles', parseInt(v) || 10)}
                />
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.showImages}
                      onChange={(e) => handleConfigChange('showImages', e.target.checked)}
                      className="w-4 h-4 rounded border-[rgba(167,139,250,0.3)] glass-card text-[#a78bfa] focus:ring-[#a78bfa]"
                    />
                    <span className="text-sm text-gray-300">Show Images</span>
                  </label>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 py-8 relative z-10">
          {/* Info Banner */}
          <div className="mb-8 p-4 rounded-xl glass-card border border-[rgba(167,139,250,0.3)]">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg glass-card">
                <BookOpen className="w-5 h-5 text-[#a78bfa]" />
              </div>
              <div>
                <h3 className="font-azonix font-semibold text-[#a78bfa]">Custom RSS Feed Display</h3>
                <p className="text-sm text-gray-400 mt-1">
                  This component replaces the original RSSApp widget. Click on any article to read it 
                  in a modal without leaving this page. All content is fetched and displayed inline.
                </p>
              </div>
            </div>
          </div>

          {/* RSS Feed Display Component */}
          <RSSFeedDisplay
            widgetId={config.widgetId}
            title={config.title}
            maxArticles={config.maxArticles}
            showImages={config.showImages}
            compact={config.compact}
          />
        </main>

        {/* Footer */}
        <footer className="border-t border-[rgba(167,139,250,0.15)] mt-12 relative z-10">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-500">
              <p>
                Powered by custom RSS feed parser - No external redirects
              </p>
              <a
                href="https://rss.app"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 hover:text-[#a78bfa] transition-colors"
              >
                <span>Powered by RSSApp</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

// Configuration Input Component
function ConfigInput({ label, value, onChange, type = 'text', placeholder = '' }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm text-gray-400">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="px-3 py-2 rounded-lg glass-card border border-[rgba(167,139,250,0.3)] text-white text-sm 
                   focus:outline-none focus:border-[#a78bfa] focus:ring-1 focus:ring-[#a78bfa]"
      />
    </div>
  );
}
